/**
 * FontAwesome SVG path utilities for rendering markers in node-canvas.
 * Extracts SVG path data from FontAwesome icons for use with Path2D API.
 */

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faBell } from '@fortawesome/sharp-solid-svg-icons/faBell';
import { faBookmark } from '@fortawesome/sharp-solid-svg-icons/faBookmark';
import { faCircle } from '@fortawesome/sharp-solid-svg-icons/faCircle';
import { faDiamond } from '@fortawesome/sharp-solid-svg-icons/faDiamond';
import { faHeart } from '@fortawesome/sharp-solid-svg-icons/faHeart';
import { faHexagon } from '@fortawesome/sharp-solid-svg-icons/faHexagon';
import { faLocationPin } from '@fortawesome/sharp-solid-svg-icons/faLocationPin';
import { faPentagon } from '@fortawesome/sharp-solid-svg-icons/faPentagon';
import { faShield } from '@fortawesome/sharp-solid-svg-icons/faShield';
import { faSquare } from '@fortawesome/sharp-solid-svg-icons/faSquare';
import { faStar } from '@fortawesome/sharp-solid-svg-icons/faStar';
import { faTriangle } from '@fortawesome/sharp-solid-svg-icons/faTriangle';

import { constants } from '@bedc/constants';
import type { types } from '@bedc/types';

export type FontAwesomeIconData = {
	path: string;
	width: number;
	height: number;
};

/**
 * Map of all marker shapes to their FontAwesome icon definitions.
 */
export const MARKER_ICON_DEFINITIONS: Record<
	types.wholeHouse.rendering.MarkerShape,
	IconDefinition
> = {
	circle: faCircle,
	square: faSquare,
	gps: faLocationPin,
	heart: faHeart,
	star: faStar,
	hexagon: faHexagon,
	triangle: faTriangle,
	diamond: faDiamond,
	shield: faShield,
	bookmark: faBookmark,
	bell: faBell,
	pentagon: faPentagon,
};

/**
 * Extract SVG path data from a FontAwesome icon definition.
 * FontAwesome icon format: [width, height, ligatures, unicode, svgPathData]
 */
function extractIconData(icon: IconDefinition): FontAwesomeIconData {
	const [width, height] = icon.icon;
	const svgPathData = icon.icon[4] as string;

	return {
		path: svgPathData,
		width,
		height,
	};
}

/**
 * Map combining icon definitions with their colors.
 * Single source of truth for frontend rendering
 */
export const MARKER_ICON_MAP: Record<
	types.wholeHouse.rendering.MarkerShape,
	{ icon: IconDefinition; color: string }
> = {
	circle: {
		icon: MARKER_ICON_DEFINITIONS.circle,
		color: constants.wholeHouse.rendering.MARKER_COLORS.circle,
	},
	square: {
		icon: MARKER_ICON_DEFINITIONS.square,
		color: constants.wholeHouse.rendering.MARKER_COLORS.square,
	},
	gps: {
		icon: MARKER_ICON_DEFINITIONS.gps,
		color: constants.wholeHouse.rendering.MARKER_COLORS.gps,
	},
	heart: {
		icon: MARKER_ICON_DEFINITIONS.heart,
		color: constants.wholeHouse.rendering.MARKER_COLORS.heart,
	},
	star: {
		icon: MARKER_ICON_DEFINITIONS.star,
		color: constants.wholeHouse.rendering.MARKER_COLORS.star,
	},
	hexagon: {
		icon: MARKER_ICON_DEFINITIONS.hexagon,
		color: constants.wholeHouse.rendering.MARKER_COLORS.hexagon,
	},
	triangle: {
		icon: MARKER_ICON_DEFINITIONS.triangle,
		color: constants.wholeHouse.rendering.MARKER_COLORS.triangle,
	},
	diamond: {
		icon: MARKER_ICON_DEFINITIONS.diamond,
		color: constants.wholeHouse.rendering.MARKER_COLORS.diamond,
	},
	shield: {
		icon: MARKER_ICON_DEFINITIONS.shield,
		color: constants.wholeHouse.rendering.MARKER_COLORS.shield,
	},
	bookmark: {
		icon: MARKER_ICON_DEFINITIONS.bookmark,
		color: constants.wholeHouse.rendering.MARKER_COLORS.bookmark,
	},
	bell: {
		icon: MARKER_ICON_DEFINITIONS.bell,
		color: constants.wholeHouse.rendering.MARKER_COLORS.bell,
	},
	pentagon: {
		icon: MARKER_ICON_DEFINITIONS.pentagon,
		color: constants.wholeHouse.rendering.MARKER_COLORS.pentagon,
	},
};

/**
 * Map of all marker shapes to their FontAwesome SVG path data.
 * Extracts path data from MARKER_ICON_DEFINITIONS for backend Path2D usage.
 */
export const fontAwesomeMarkerPaths: Record<
	types.wholeHouse.rendering.MarkerShape,
	FontAwesomeIconData
> = {
	circle: extractIconData(MARKER_ICON_DEFINITIONS.circle),
	square: extractIconData(MARKER_ICON_DEFINITIONS.square),
	gps: extractIconData(MARKER_ICON_DEFINITIONS.gps),
	heart: extractIconData(MARKER_ICON_DEFINITIONS.heart),
	star: extractIconData(MARKER_ICON_DEFINITIONS.star),
	hexagon: extractIconData(MARKER_ICON_DEFINITIONS.hexagon),
	triangle: extractIconData(MARKER_ICON_DEFINITIONS.triangle),
	diamond: extractIconData(MARKER_ICON_DEFINITIONS.diamond),
	shield: extractIconData(MARKER_ICON_DEFINITIONS.shield),
	bookmark: extractIconData(MARKER_ICON_DEFINITIONS.bookmark),
	bell: extractIconData(MARKER_ICON_DEFINITIONS.bell),
	pentagon: extractIconData(MARKER_ICON_DEFINITIONS.pentagon),
};
